// @ts-check
import React, { createContext, useContext, useEffect, useReducer } from "react";
import { mainReducer, initMainState } from "./reducer";
import moment from "moment";
import { getStudyList } from "../../service/study.service";

/**
 * @typedef {Object} MainContextType
 * @property {import("./reducer").initialStateType} state
 * @property {React.Dispatch<import("./reducer").MainAction>} dispatch
 * @property {() => Promise<void>} syncLista
 * @property {() => Promise<void>} silentSyncLista
 */

const MainContext = createContext(/** @type {MainContextType | null} */ (null));

// Hook personalizado para facilitar su consumo
/**
 * @returns {MainContextType}
 */
export const useMainContext = () => {
  const context = useContext(MainContext);
  if (!context) {
    throw new Error("useMainContext must be used within a MainProvider");
  }
  return context;
};

// Componente Provider que encapsulará a Main
/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const MainProvider = ({ children }) => {
  const defaultFechas = {
    inicio: moment().subtract(1, "d").format("YYYYMMDD"),
    final: moment().format("YYYYMMDD"),
  };

  const [state, dispatch] = useReducer(
    mainReducer,
    defaultFechas,
    initMainState,
  );
  const fetchcontrol = new AbortController();

  const syncLista = async () => {
    if (!state.estudios.sync && !state.estudios.silentSync) {
      dispatch({ type: "SYNC_START" });
      try {
        const res = await getStudyList(
          state.filtros.fechas.inicio,
          state.filtros.fechas.final,
        );
        dispatch({ type: "FETCH_SUCCESS", payload: res });
      } catch (error) {
        dispatch({ type: "FETCH_ERROR" });
      }
    }
  };

  const silentSyncLista = async () => {
    if (!state.estudios.sync && !state.estudios.silentSync) {
      dispatch({ type: "SILENT_SYNC_START" });
      try {
        const res = await getStudyList(
          state.filtros.fechas.inicio,
          state.filtros.fechas.final,
        );
        dispatch({ type: "FETCH_SUCCESS", payload: res });
      } catch (error) {
        dispatch({ type: "FETCH_ERROR" });
      }
    }
  };

  useEffect(() => {
    if (!state.filtros.fechas.ready) return;

    getStudyList(state.filtros.fechas.inicio, state.filtros.fechas.final)
      .then((res) => {
        dispatch({ type: "FETCH_SUCCESS", payload: res });
      })
      .catch((err) => {
        console.error("Error fetching studies:", err);
        dispatch({ type: "FETCH_ERROR" });
      });

    return () => {
      dispatch({ type: "FETCH_START" });
      fetchcontrol.abort();
    };
  }, [
    state.filtros.fechas.inicio,
    state.filtros.fechas.final,
    state.filtros.fechas.ready,
  ]);

  // === AUTOSYNC INTERVAL ===
  useEffect(() => {
    if (!state.filtros.fechas.ready || !state.configuracion.autoSync) return;

    // Ejecutar la sincronización silenciosa cada 30 segundos
    const syncInterval = setInterval(() => {
      silentSyncLista();
    }, 30000);

    return () => {
      clearInterval(syncInterval);
    };
    // El efecto se limpiará y reiniciará cuando cambien las fechas o el estado de autoSync
  }, [
    state.filtros.fechas.inicio,
    state.filtros.fechas.final,
    state.filtros.fechas.ready,
    state.configuracion.autoSync,
  ]);

  // === SINCRONIZACIÓN DE ESTADOS HACIA SESSIONSTORAGE ===
  useEffect(() => {
    if (state.filtros.fechas.ready) {
      window.sessionStorage.setItem(
        "filtrosmain",
        JSON.stringify({
          ...state.filtros,
          autoSync: state.configuracion.autoSync,
        }),
      );
    }
  }, [state.filtros, state.configuracion.autoSync]);

  // Valores expuestos globalmente
  const value = { state, dispatch, syncLista, silentSyncLista };

  return <MainContext.Provider value={value}>{children}</MainContext.Provider>;
};
